import { Permission } from '@Constants/Permission.g';
import { STAMMDATEN_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { useHasPermission } from '@Contexts/appContext';
import { ApiCostBearer } from '@Model';
import {
    Card,
    EditFrameMode,
    EditFrameShell,
    FrameEditBase,
    Grid,
    GridColumns,
    IFrameBodyProps,
    IFrameEditShellProps,
    TextInput,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useTranslate,
} from '@synqo/frontend-component-library';
import { useBadges } from '@Hooks/useBadges';
import { useParams } from 'react-router';

export function FrameEditCostBearer(props: IFrameEditShellProps<ApiCostBearer>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiCostBearer>
            apiResourcePath={`${STAMMDATEN_API}/cost_bearers`}
            renderer={FrameEditCostBearerBody}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            canEdit={props.canEdit !== false && hasPermission(Permission.StammdatenCostBearerUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.StammdatenCostBearerCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.StammdatenCostBearerDelete)}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

function FrameEditCostBearerBody(props: IFrameBodyProps<ApiCostBearer>) {
    const i18n = useTranslate('');

    const binder = useGuiBinder<ApiCostBearer>({
        ...props,
        canEdit: props.mode !== EditFrameMode.View,
    });

    const costBearerBadges = useBadges(props.data);

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Kostenträger', props.mode),
                resourceLabel: 'Kostenträger',
                ...props.events,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card
                name="PrimaryData"
                border={'withoutBorder'}
                label="Allgemeine Informationen"
                badges={costBearerBadges}
            >
                <Grid>
                    <GridColumns spans={[4, 4, 4]}>
                        <TextInput {...binder.bindToProp<string>('label')} label={i18n('Bezeichnung')} />
                        <TextInput {...binder.bindToProp<string>('shorthand')} label={i18n('Suchcode')} />
                        <TextInput {...binder.bindToProp<string>('number')} label={i18n('Nummer')} />
                    </GridColumns>
                </Grid>
                <TextInput {...binder.bindToProp<string>('remarks')} inputSize={3} label={i18n('Bemerkung')} />
            </Card>
        </EditFrameShell>
    );
}
